import { useCallback, useRef, useState } from 'react';

import {
  mediaReplace,
  mediaUpload,
  mediaUploadChunk,
  mediaUploadComplete,
  mediaUploadStart,
  mediaUploadStatus,
} from '@/shared/api/generated/media/media';
import type { AssetBody } from '@/shared/api/generated/model';
import { ApiError, NetworkError } from '@/shared/api/http';

/** Больше этого — частями: одним запросом сервер примет до 20 МБ. */
const SINGLE_LIMIT = 20 * 1024 * 1024;
/** Сколько раз пробовать кусок, пока сеть моргает. */
const CHUNK_RETRIES = 4;

export type Environment = 'production' | 'development';

export interface UploadTask {
  id: string;
  name: string;
  size: number;
  sent: number;
  status: 'queued' | 'uploading' | 'done' | 'failed';
  error?: string;
}

/**
 * Загрузка файлов в медиатеку: небольшие — одним запросом, большие (видео)
 * — частями по 8 МБ. Оборвалась связь — кусок повторяется; совсем оборвалась
 * — загрузчик спрашивает сервер, сколько принято, и продолжает оттуда, а не
 * с нуля (docs/research/09-media.md).
 *
 * Файлы идут по одному: отказ по одному — слишком большому, не того вида —
 * не отменяет остальные, и видно, что именно не прошло.
 */
export function useUploader(
  siteID: string,
  env: Environment,
  onUploaded: (asset: AssetBody) => void,
) {
  const [tasks, setTasks] = useState<UploadTask[]>([]);
  const running = useRef(false);

  const patch = (id: string, change: Partial<UploadTask>) => {
    setTasks((prev) => prev.map((task) => (task.id === id ? { ...task, ...change } : task)));
  };

  const send = useCallback(
    (files: FileList | File[] | null, folderID: string | undefined) => {
      if (files === null || files.length === 0) {
        return;
      }

      const queue = Array.from(files).map((file) => ({
        file,
        task: {
          id: crypto.randomUUID(),
          name: file.name,
          size: file.size,
          sent: 0,
          status: 'queued' as const,
        },
      }));

      setTasks((prev) => [
        ...prev.filter((task) => task.status !== 'done'),
        ...queue.map((item) => item.task),
      ]);

      void (async () => {
        // Очередь одна: второй выбор файлов дождётся первого.
        while (running.current) {
          await new Promise((resolve) => setTimeout(resolve, 200));
        }

        running.current = true;

        for (const { file, task } of queue) {
          patch(task.id, { status: 'uploading' });

          try {
            const asset =
              file.size <= SINGLE_LIMIT
                ? await single(siteID, env, file, folderID)
                : await chunked(siteID, env, file, folderID, (sent) => {
                    patch(task.id, { sent });
                  });

            patch(task.id, { status: 'done', sent: file.size });
            onUploaded(asset);
          } catch (cause) {
            patch(task.id, {
              status: 'failed',
              error:
                cause instanceof ApiError ? cause.detail : 'Связь оборвалась — попробуйте ещё раз',
            });
          }
        }

        running.current = false;
      })();
    },
    [siteID, env, onUploaded],
  );

  const clear = () => {
    setTasks((prev) =>
      prev.filter((task) => task.status === 'uploading' || task.status === 'queued'),
    );
  };

  return {
    tasks,
    send,
    clear,
    busy: tasks.some((task) => task.status === 'uploading' || task.status === 'queued'),
  };
}

async function single(
  siteID: string,
  env: Environment,
  file: File,
  folder: string | undefined,
): Promise<AssetBody> {
  const response = await mediaUpload(siteID, env, { file }, folder === undefined ? {} : { folder });

  if (response.status !== 201) {
    throw new Error('Неожиданный ответ на загрузку');
  }

  return response.data;
}

async function chunked(
  siteID: string,
  env: Environment,
  file: File,
  folder: string | undefined,
  progress: (sent: number) => void,
  replace?: string,
): Promise<AssetBody> {
  const meta = await mediaMeta(file);

  const started = await mediaUploadStart(siteID, env, {
    name: file.name,
    size: file.size,
    ...(folder === undefined ? {} : { folder_id: folder }),
    ...(replace === undefined ? {} : { replace_id: replace }),
    ...meta,
  });

  if (started.status !== 201) {
    throw new Error('Неожиданный ответ на начало загрузки');
  }

  const id = started.data.id;
  const size = started.data.chunk_size;
  let offset = started.data.received;

  while (offset < file.size) {
    const piece = file.slice(offset, Math.min(offset + size, file.size));

    offset = await sendChunk(id, piece, offset);
    progress(offset);
  }

  const done = await mediaUploadComplete(id);

  if (done.status !== 201) {
    throw new Error('Неожиданный ответ на сборку файла');
  }

  return done.data;
}

/**
 * Заменить содержимое файла: тот же id, новое содержимое — во всех
 * записях разом. Небольшой файл — одним запросом, большой — частями, как
 * при загрузке, с той же докачкой.
 */
export async function replaceFile(
  siteID: string,
  env: Environment,
  mediaID: string,
  file: File,
  progress: (sent: number) => void,
): Promise<AssetBody> {
  if (file.size > SINGLE_LIMIT) {
    return chunked(siteID, env, file, undefined, progress, mediaID);
  }

  const response = await mediaReplace(mediaID, { file });

  if (response.status !== 200) {
    throw new Error('Неожиданный ответ на замену');
  }

  progress(file.size);

  return response.data;
}

/**
 * Кусок с повторами. Сеть моргнула — пауза и ещё раз. Сервер ответил «не с
 * того места» (кусок уже принят, ответ потерялся) — спрашиваем, сколько
 * принято, и продолжаем оттуда.
 */
async function sendChunk(id: string, piece: Blob, offset: number): Promise<number> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const response = await mediaUploadChunk(id, piece, { offset });

      if (response.status === 200) {
        return response.data.received;
      }
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 409) {
        const status = await mediaUploadStatus(id);

        if (status.status === 200) {
          return status.data.received;
        }
      }

      if (!(cause instanceof NetworkError) || attempt >= CHUNK_RETRIES) {
        throw cause;
      }

      await new Promise((resolve) => setTimeout(resolve, 1000 * 2 ** attempt));
    }
  }
}

/**
 * Длительность и размер видео или аудио — их узнаёт браузер: сервер видео не
 * разбирает. Не получилось — не беда, загрузка пойдёт без них.
 */
function mediaMeta(file: File): Promise<{ duration?: number; width?: number; height?: number }> {
  const kind = file.type.startsWith('video/')
    ? 'video'
    : file.type.startsWith('audio/')
      ? 'audio'
      : '';

  if (kind === '') {
    return Promise.resolve({});
  }

  return new Promise((resolve) => {
    const element = document.createElement(kind);
    const url = URL.createObjectURL(file);
    const finish = (value: { duration?: number; width?: number; height?: number }) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };

    element.preload = 'metadata';
    element.onloadedmetadata = () => {
      const duration = Number.isFinite(element.duration) ? element.duration : undefined;
      const video = element instanceof HTMLVideoElement ? element : undefined;

      finish({
        ...(duration === undefined ? {} : { duration }),
        ...(video !== undefined && video.videoWidth > 0
          ? { width: video.videoWidth, height: video.videoHeight }
          : {}),
      });
    };
    element.onerror = () => {
      finish({});
    };
    element.src = url;
  });
}
